import { h } from '@/components/dom';
import { checkbox, colorInput, note, section, select, slider } from '@/components/controls';
import type { App } from './App';
import type { BlendMode } from '@/state/appState';

export function createRenderPanel(app: App): HTMLElement {
  const r = () => app.state.render;
  const thickness = slider({ label: 'String thickness', min: 0.1, max: 6, step: 0.05, value: r().stringThickness, format: (v) => `${v.toFixed(2)} px`, onInput: (v) => app.updateRender((s) => (s.stringThickness = v)) });
  const opacity = slider({ label: 'String opacity', min: 0.02, max: 1, step: 0.01, value: r().stringOpacity, onInput: (v) => app.updateRender((s) => (s.stringOpacity = v)) });
  const color = colorInput({ label: 'String colour', value: r().stringColor, onChange: (v) => app.updateRender((s) => (s.stringColor = v)) });
  const background = colorInput({ label: 'Background', value: r().backgroundColor, onChange: (v) => app.updateRender((s) => (s.backgroundColor = v)) });
  const blend = select<BlendMode>({ label: 'Blend mode', value: app.state.ui.blendMode, options: [{ value: 'source-over', label: 'Normal' }, { value: 'multiply', label: 'Multiply' }, { value: 'darken', label: 'Darken' }], onChange: (v) => app.updateUi({ blendMode: v }) });
  const roughness = slider({ label: 'Thread roughness', min: 0, max: 1, step: 0.01, value: app.state.ui.stringRoughness, hint: 'Per-string opacity variation that mimics real thread.', onInput: (v) => app.updateUi({ stringRoughness: v }) });
  const brightness = slider({ label: 'Thread brightness', min: 0, max: 0.9, step: 0.01, value: app.state.ui.stringBrightness, onInput: (v) => app.updateUi({ stringBrightness: v }) });
  const showPins = checkbox({ label: 'Show pins', value: app.state.ui.showPins, onChange: (v) => app.updateUi({ showPins: v }) });
  const showIds = checkbox({ label: 'Show pin numbers', value: app.state.ui.showPinIds, onChange: (v) => app.updateUi({ showPinIds: v }) });
  const pinColor = colorInput({ label: 'Pin colour', value: r().pinColor, onChange: (v) => app.updateRender((s) => (s.pinColor = v)) });
  const pinRadius = slider({ label: 'Pin radius', min: 0.5, max: 8, step: 0.1, value: r().pinRadius, format: (v) => `${v.toFixed(1)} px`, onInput: (v) => app.updateRender((s) => (s.pinRadius = v)) });
  const exportPins = checkbox({ label: 'Include pins in exports', value: r().showPins, onChange: (v) => app.updateRender((s) => (s.showPins = v)) });
  const underlay = slider({ label: 'Underlay opacity', min: 0, max: 1, step: 0.05, value: app.state.ui.underlayOpacity, hint: 'Opacity of the target / difference image shown under the strings.', onInput: (v) => app.updateUi({ underlayOpacity: v }) });
  const renderer3d = checkbox({ label: '3D presentation (Three.js)', value: app.state.ui.renderer === '3d', onChange: (v) => app.updateUi({ renderer: v ? '3d' : '2d' }) });

  app.store.subscribe((s, p) => {
    if (s.render !== p.render || s === p) {
      thickness.setValue(s.render.stringThickness);
      opacity.setValue(s.render.stringOpacity);
      color.setValue(s.render.stringColor);
      background.setValue(s.render.backgroundColor);
      pinColor.setValue(s.render.pinColor);
      pinRadius.setValue(s.render.pinRadius);
      exportPins.setValue(s.render.showPins);
    }
    if (s.ui !== p.ui || s === p) {
      blend.setValue(s.ui.blendMode);
      roughness.setValue(s.ui.stringRoughness);
      brightness.setValue(s.ui.stringBrightness);
      showPins.setValue(s.ui.showPins);
      showIds.setValue(s.ui.showPinIds);
      underlay.setValue(s.ui.underlayOpacity);
      renderer3d.setValue(s.ui.renderer === '3d');
    }
  }, { immediate: true });

  return h(
    'div',
    null,
    section('Thread', [thickness.element, opacity.element, color.element, background.element, blend.element, roughness.element, brightness.element, note('Overlapping threads darken multiplicatively: 1 string is light, 20 are nearly black.')]),
    section('Pins', [showPins.element, showIds.element, pinColor.element, pinRadius.element, exportPins.element]),
    section('View', [underlay.element, renderer3d.element]),
  );
}
